/**
 * コードブロックの初期化: highlight.js 適用・行番号付与・行ハイライト・コピー
 * （05_ui.md §6.2 / 04_sequence.md §6）。
 */
import { showToast } from "../ui/toast.js";
import { highlightElement } from "./highlight.js";

const COPY_ICON_RESTORE_MS = 2000; // spec.md「約 2 秒チェックマークに変わる」

function parseHighlightLines(raw: string | undefined): Set<number> {
  const result = new Set<number>();
  if (!raw) return result;
  for (const token of raw.split(",")) {
    const n = Number.parseInt(token.trim(), 10);
    if (Number.isFinite(n)) result.add(n);
  }
  return result;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

interface OpenTag {
  tag: string;
  className: string;
}

/**
 * ハイライト後の DOM を行単位の HTML 文字列配列に分割する。開いたままのハイライト span は
 * 行ごとに閉じ直し、次の行冒頭で再度開く（05_ui.md B4）。
 */
function splitIntoLines(code: HTMLElement): string[] {
  const lines: string[] = [];
  let current = "";
  const stack: OpenTag[] = [];

  const openMarkup = (): string => stack.map((t) => `<${t.tag} class="${t.className}">`).join("");
  const closeMarkup = (): string =>
    stack
      .slice()
      .reverse()
      .map((t) => `</${t.tag}>`)
      .join("");

  function endLine(): void {
    lines.push(current + closeMarkup());
    current = openMarkup();
  }

  function walk(node: ChildNode): void {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? "";
      const parts = text.split("\n");
      parts.forEach((part, i) => {
        current += escapeHtml(part);
        if (i < parts.length - 1) endLine();
      });
    } else if (node instanceof HTMLElement) {
      const tag = node.tagName.toLowerCase();
      const className = node.className;
      current += `<${tag}${className ? ` class="${className}"` : ""}>`;
      stack.push({ tag, className });
      Array.from(node.childNodes).forEach(walk);
      stack.pop();
      current += `</${tag}>`;
    }
  }

  Array.from(code.childNodes).forEach(walk);
  lines.push(current);
  return lines;
}

function applyLineStructure(code: HTMLElement, showLineNumbers: boolean, highlightLines: Set<number>): void {
  const lines = splitIntoLines(code);
  code.innerHTML = lines
    .map((line, i) => {
      const lineNo = i + 1;
      const highlighted = highlightLines.has(lineNo) ? " is-highlighted" : "";
      const lineNoHtml = showLineNumbers
        ? `<span class="lms-md-code__lineno" aria-hidden="true">${lineNo}</span>`
        : "";
      return `<span class="lms-md-code__line${highlighted}">${lineNoHtml}<span class="lms-md-code__text">${line}</span></span>`;
    })
    // 行は改行文字で連結しない。`.lms-md-code__line` は display:block で縦積みされるため、
    // <pre>（white-space:pre）内で "\n" を挟むと block 要素の間に余分な空行が生まれ、
    // 行間が実質 2 倍に膨らんでしまう（モックとの行間差の主因）。
    .join("");
}

async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // 権限拒否などの場合はフォールバックに落とす
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  let ok: boolean;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  textarea.remove();
  return ok;
}

function initCopyButton(root: HTMLElement): void {
  const button = root.querySelector<HTMLButtonElement>("[data-lms-code-copy]");
  if (!button) return;

  const copyIcon = button.querySelector<HTMLElement>('[data-lms-code-icon="copy"]');
  const doneIcon = button.querySelector<HTMLElement>('[data-lms-code-icon="done"]');
  let restoreTimer: number | undefined;

  button.addEventListener("click", () => {
    const source = root.dataset["lmsCodeSource"] ?? "";
    void copyText(source).then((ok) => {
      if (!ok) {
        showToast("コピーに失敗しました", "error");
        return;
      }
      copyIcon?.setAttribute("hidden", "");
      doneIcon?.removeAttribute("hidden");
      button.setAttribute("aria-label", "コピーしました");

      if (restoreTimer !== undefined) {
        window.clearTimeout(restoreTimer);
      }
      restoreTimer = window.setTimeout(() => {
        copyIcon?.removeAttribute("hidden");
        doneIcon?.setAttribute("hidden", "");
        button.setAttribute("aria-label", "コードをコピー");
      }, COPY_ICON_RESTORE_MS);
    });
  });
}

function initSingle(root: HTMLElement): void {
  if (root.dataset["lmsCodeReady"] === "1") return;
  root.dataset["lmsCodeReady"] = "1";

  const code = root.querySelector<HTMLElement>("code");
  const lang = root.dataset["lmsCodeLang"] ?? null;
  const showLineNumbers = "lmsCodeLineNumbers" in root.dataset;
  const highlightLines = parseHighlightLines(root.dataset["lmsCodeHighlight"]);

  if (code && lang) {
    highlightElement(code, lang);
  }
  // 行番号・行ハイライトが無いコードブロックも常に行構造化する。__line でラップしないと
  // 行ごとの左右パディング（padding:0 12px）が付かず、コードが本体背景の左端に貼り付いて
  // 左オフセットが 0 になってしまう（行番号なし時の見た目差の原因）。
  if (code) {
    applyLineStructure(code, showLineNumbers, highlightLines);
  }

  if ("lmsCodeNocopy" in root.dataset) {
    root.querySelector("[data-lms-code-copy]")?.remove();
  } else {
    initCopyButton(root);
  }
}

export function initCodeBlocks(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>("[data-lms-code]").forEach((root) => {
    initSingle(root);
  });
}
